import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Graph from "../components/Graph/Graph";
import { fetchAssetById } from "../helpers/apiHelper";
import { AssetResponse, AssetStatus } from "../types/statusTypes";
import ErrorView from "./ErrorView";
import Loading from "../components/Loading";
import { useApiErrorHandler } from "../hooks/useApiErrorHandler";
import RestartDialog from "../components/Dialog/RestartDialog";
import RevokeDialog from "../components/Dialog/RevokeDialog";
import ItemDetailedViewFooter from "../components/ItemDetailedView/ItemDetailedViewFooter";
import ItemDetailedViewHeader from "../components/ItemDetailedView/ItemDetailedViewHeader";
import AuditLogDialog from "../components/Dialog/AuditLogDialog";
import { getAssetStatus } from "../helpers/statusMapper";

const MINUTE_IN_MS = 60_000;

export default function ItemDetailedView() {
  const { id } = useParams();
  const [alert, setAlert] = useState("");
  const [isReloadingInBackground, setIsReloadingInBackground] = useState(false);
  const { loading, error, response, fetchData } = useApiErrorHandler<AssetResponse>();

  const loadAsset = useCallback(() => {
    if (id) {
      fetchData(fetchAssetById(id));
    }
  }, [fetchData, id]);

  useEffect(() => {
    setIsReloadingInBackground(false);
    loadAsset();
    setAlert("");
  }, [id, loadAsset]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsReloadingInBackground(true);
      loadAsset();
    }, MINUTE_IN_MS);
    return () => clearInterval(interval);
  }, [loadAsset]);

  function handleSuccess() {
    setIsReloadingInBackground(false);
    loadAsset();
    setAlert("");
  }

  function handleFailure(err: string) {
    setIsReloadingInBackground(false);
    loadAsset();
    setAlert(err);
  }

  const assetStatus = response?.jobs ? getAssetStatus(response.jobs) : AssetStatus.Active;

  return (
    <div className="h-100 w-100 d-flex flex-column gap-3 py-3 px-5">
      <ItemDetailedViewHeader
        headerText={(response && response.version) || id}
        loading={loading}
        isReloadingInBackground={isReloadingInBackground}
      />
      {loading && !isReloadingInBackground && (<Loading />)}
      {!loading && error && (
        <ErrorView message={error} hasHomeButton={true} />
      )}
      {(!loading || isReloadingInBackground) && response && id && (
        <>
          <Graph jobs={response.jobs} />
          <RestartDialog assetId={id} onSuccess={handleSuccess} onFailure={handleFailure} />
          <RevokeDialog
            assetId={id}
            assetStatus={assetStatus}
            onSuccess={handleSuccess}
            onFailure={handleFailure}
          />
          <AuditLogDialog assetId={id} />
          <ItemDetailedViewFooter
            alert={alert}
            hasRevokeButton={assetStatus === AssetStatus.Active}
          />
        </>
      )}
    </div>
  );
}
